import React, { useState } from 'react';
import { typeArticleService } from '../../../../services/typeArticleService';
import { useTheme } from '../../../../contexts/ThemeContext';

const DeleteTypeArticleModal = ({ isOpen, typeArticle, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const handleDelete = async () => {
    if (confirmText !== typeArticle.designation) {
      setError('Le nom saisi ne correspond pas');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await typeArticleService.deleteTypeArticle(typeArticle.id);

      if (result.success) {
        onSuccess();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Erreur lors de la suppression du type d\'article');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setConfirmText('');
    onClose();
  };

  if (!isOpen || !typeArticle) return null;

  const getFamilleInfo = (familleId) => {
    const familleData = {
      1: { name: 'Extincteurs', icon: 'fas fa-fire-extinguisher' },
      2: { name: 'Types d\'extinction', icon: 'fas fa-spray-can' },
      3: { name: 'Équipements', icon: 'fas fa-tools' }
    };
    return familleData[familleId] || familleData[1];
  };

  const familleInfo = getFamilleInfo(typeArticle.familleId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`px-6 py-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-red-600 to-red-700'}`}>
          <div className="flex items-center">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
              <i className="fas fa-exclamation-triangle text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Confirmer la suppression</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-red-100'}`}>Cette action est irréversible</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className={`mb-6 p-4 rounded-lg border-l-4 flex items-center ${
              isDarkMode
                ? 'bg-gray-900 border-gray-800 text-gray-200'
                : 'bg-red-50 border-red-500 text-red-700'
            }`}>
              <i className={`fas fa-exclamation-circle mr-2 ${isDarkMode ? 'text-gray-400' : 'text-red-500'}`}></i>
              <span>{error}</span>
            </div>
          )}

          {/* Type Article Info */}
          <div className={`mb-6 flex items-center p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-200'}`}>
              <i className={`${familleInfo.icon} text-lg`}></i>
            </div>
            <div>
              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{typeArticle.designation}</h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{familleInfo.name}</p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>ID: {typeArticle.id}</p>
            </div>
          </div>

          {/* Warning Message */}
          <div className={`mb-6 p-4 rounded-lg border-l-4 flex ${
            isDarkMode
              ? 'bg-gray-900 border-gray-800 text-gray-200'
              : 'bg-amber-50 border-amber-500 text-amber-700'
          }`}>
            <i className={`fas fa-exclamation-triangle mr-2 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-amber-500'}`}></i>
            <div>
              <h4 className={`font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-amber-800'}`}>Attention</h4>
              <p className="text-sm">
                Êtes-vous sûr de vouloir supprimer ce type d'article ? Cette action supprimera également :
              </p>
              <ul className="mt-2 text-sm list-disc list-inside space-y-1">
                <li>Tous les articles associés</li>
                <li>L'historique des utilisations</li>
                <li>Les références dans les interventions</li>
              </ul>
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Pour confirmer, tapez <span className={`font-semibold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{typeArticle.designation}</span>
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-red-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-red-500'}`}
              placeholder="Tapez la désignation..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleClose}
              disabled={loading}
              className={`px-6 py-3 rounded-lg transition-colors font-medium ${
                isDarkMode
                  ? 'text-gray-200 bg-gray-700 border border-gray-600 hover:bg-gray-600'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button
              onClick={handleDelete}
              disabled={loading || confirmText !== typeArticle.designation}
              className={`px-6 py-3 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                loading || confirmText !== typeArticle.designation
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isDarkMode
                  ? 'bg-red-700 hover:bg-red-800 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Suppression...
                </>
              ) : (
                <>
                  <i className="fas fa-trash mr-2"></i>
                  Supprimer définitivement
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteTypeArticleModal;